import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';

import jwt_decode from 'jwt-decode';
import { ApiErrorHandlerService } from '../shared/services/api-error-handler.service';
import { User } from '@serpro/ngx-suite-rfb';
import { environment } from 'src/environments/environment';
import { appRoute } from '../shared/consts/app-routes';
import { Retorno } from '../shared/model/retorno.model';

export interface TokenPayload {
    sub: string;
    exp: number;
    roleNames: string;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private baseUrl = `${environment.apiUrl}/auth`;

    private userChangeSubject = new BehaviorSubject<User | undefined>(undefined);
    private _loggedinUser$ = this.userChangeSubject.asObservable();

    private tokenChangedSubject = new Subject<TokenPayload>();
    tokenChanged$ = this.tokenChangedSubject.asObservable();

    token: string | undefined;
    private tokenPayload: TokenPayload;

    constructor(
        private router: Router,
        private http: HttpClient,
        private errorHandler: ApiErrorHandlerService) {
    }

    get loggedInUser$(): Observable<User | undefined> {
        if (!this.token) {
            this.storeToken(localStorage.getItem('token'));
            if (!this.isTokenExpired()) {
                this.loadLoggedInUser();
            }
        }
        return this._loggedinUser$;
    }

    private loadLoggedInUser() {
        this.http.get<User>(`${this.baseUrl}/usuario-logado`)
            .pipe(catchError(this.errorHandler.handle()))
            .subscribe(user => {
                this.raiseUserChanged(user);
            });
    }

    private raiseUserChanged(user: User | undefined): void {
        return this.userChangeSubject.next(user);
    }

    logarComCertificado(token: string) {
        this.storeToken(token);
        this.loadLoggedInUser();
    }

    public storeToken(token: string | undefined | null) {
        
        if (!token || token == null) {
            this.token = undefined;
            localStorage.removeItem('token');
            return;
        }
        
        this.token = token;
        this.tokenPayload = this.getDecodedAccessToken(token);

        if (token && !this.isTokenExpired()) {
            localStorage.setItem('token', token);
            this.tokenChangedSubject.next(this.tokenPayload);
        } else {
            this.token = undefined;
            localStorage.removeItem('token');
        }
    }

    private isTokenExpired(): boolean {

        if (!this.tokenPayload) {
            return true;
        }

        const now = new Date().getTime() / 1000;
        return now > this.tokenPayload.exp;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getDecodedAccessToken(token: string): any {
        try {
            return jwt_decode(token);
        } catch (Error) {
            return { sub: undefined, exp: 0 };
        }
    }

    private clearToken() {
        this.storeToken(undefined);
    }

    logout() {
        this.clearToken();
        this.router.navigate([`${appRoute.login}`]);
        this.raiseUserChanged(undefined);
    }

    isAuthenticated() {
        if (environment.apiUrl.includes("localhost")) { 
            console.log('[ATENCAO] LOGIN LOCAL!');
            this.logarComCertificado("TOKEN_LOCAL");
            return true;
        }
        if (!this.token) {
            return false;
        }
        return !this.isTokenExpired();
    }

    get rolesName(): string {
        return this.tokenPayload.roleNames;
    }

    public getUrlAuthCertificado(): Observable<Retorno> {
        return this.http.get<Retorno>(`${this.baseUrl}/certificado/url`).pipe(catchError(this.errorHandler.handle()));
    }
}
